// D:\QR\QR-WEB\dashboard\src\components\Business\ExchangeHistory.jsx
// Версия: 1.0 - История пополнений и выводов
// Дата: 21.11.2025 16:05
// Время на разработку: 2-3 часа

import React, { useState, useEffect } from 'react';
import './ExchangeHistory.css';

const ExchangeHistory = () => {
  const [deposits, setDeposits] = useState([]);
  const [withdrawals, setWithdrawals] = useState([]);
  const [activeTab, setActiveTab] = useState('deposits'); // deposits или withdrawals
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [statusFilter, setStatusFilter] = useState('all');
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 10;

  // Загрузить данные при монтировании
  useEffect(() => {
    fetchData();
  }, [activeTab]);

  // Получить данные с backend
  const fetchData = async () => {
    setLoading(true);
    setError(null);

    try {
      const token = localStorage.getItem('authToken');
      
      if (!token) {
        setError('Ошибка авторизации');
        setLoading(false);
        return;
      }

      const endpoint = activeTab === 'deposits' 
        ? '/api/business/exchange/deposits'
        : '/api/business/exchange/withdrawals';

      const response = await fetch(
        `${process.env.REACT_APP_API_URL}${endpoint}?page=1&limit=100`,
        {
          headers: { 'Authorization': `Bearer ${token}` }
        }
      );

      if (!response.ok) {
        throw new Error('Ошибка при загрузке данных');
      }

      const data = await response.json();
      
      if (activeTab === 'deposits') {
        setDeposits(data.data || []);
      } else {
        setWithdrawals(data.data || []);
      }
    } catch (err) {
      setError(`❌ Ошибка: ${err.message}`);
      console.error('Fetch error:', err);
    } finally {
      setLoading(false);
    }
  };

  // Фильтровать данные по статусу
  const getFilteredData = () => {
    const data = activeTab === 'deposits' ? deposits : withdrawals;
    
    if (statusFilter === 'all') {
      return data;
    }
    
    return data.filter(item => item.status === statusFilter);
  };

  // Пагинация
  const filteredData = getFilteredData();
  const totalPages = Math.ceil(filteredData.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const paginatedData = filteredData.slice(startIndex, startIndex + itemsPerPage);

  // Форматировать дату
  const formatDate = (dateString) => {
    if (!dateString) return '-';
    const date = new Date(dateString);
    return date.toLocaleDateString('ru-RU') + ' ' + date.toLocaleTimeString('ru-RU', { hour: '2-digit', minute: '2-digit' });
  };

  // Статус badge
  const getStatusBadge = (status) => {
    const statusMap = {
      'pending': { color: '#f39c12', icon: '⏳', text: 'На рассмотрении' },
      'confirmed': { color: '#3498db', icon: '✓', text: 'Подтверждено' },
      'completed': { color: '#27ae60', icon: '✅', text: 'Завершено' },
      'failed': { color: '#e74c3c', icon: '✗', text: 'Отклонено' },
      'approved': { color: '#3498db', icon: '✓', text: 'Одобрено' },
      'processing': { color: '#9b59b6', icon: '⟳', text: 'Обработка' },
      'rejected': { color: '#e74c3c', icon: '✗', text: 'Отклонено' }
    };
    
    return statusMap[status] || { color: '#95a5a6', icon: '?', text: status };
  };

  // Уникальные статусы для фильтра
  const getAvailableStatuses = () => {
    const data = activeTab === 'deposits' ? deposits : withdrawals;
    const statuses = [...new Set(data.map(item => item.status))];
    return statuses;
  };

  return (
    <div className="exchange-history">
      <h2>💼 История пополнений и выводов</h2>

      {/* Табы */}
      <div className="tabs">
        <button
          className={`tab ${activeTab === 'deposits' ? 'active' : ''}`}
          onClick={() => {
            setActiveTab('deposits');
            setCurrentPage(1);
            setStatusFilter('all');
          }}
        >
          💰 Покупки DAR за EUR ({deposits.length})
        </button>
        <button
          className={`tab ${activeTab === 'withdrawals' ? 'active' : ''}`}
          onClick={() => {
            setActiveTab('withdrawals');
            setCurrentPage(1);
            setStatusFilter('all');
          }}
        >
          💸 Продажи DAR за EUR ({withdrawals.length})
        </button>
      </div>

      {/* Фильтр по статусу */}
      <div className="filters">
        <label>Фильтр по статусу:</label>
        <select 
          value={statusFilter} 
          onChange={(e) => {
            setStatusFilter(e.target.value);
            setCurrentPage(1);
          }}
        >
          <option value="all">Все операции</option>
          {getAvailableStatuses().map(status => {
            const badge = getStatusBadge(status);
            return (
              <option key={status} value={status}>
                {badge.text}
              </option>
            );
          })}
        </select>
      </div>

      {/* Loading */}
      {loading && (
        <div className="loading">
          <div className="spinner"></div>
          <p>Загрузка данных...</p>
        </div>
      )}

      {/* Error */}
      {error && !loading && (
        <div className="alert alert-error">
          {error}
          <button onClick={fetchData}>🔄 Повторить</button>
        </div>
      )}

      {/* Таблица */}
      {!loading && !error && paginatedData.length > 0 && (
        <>
          <div className="table-container">
            <table className="history-table">
              <thead>
                <tr>
                  <th>Дата</th>
                  <th>Тип операции</th>
                  <th>Сумма</th>
                  <th>Курс</th>
                  <th>Статус</th>
                  <th>Детали</th>
                </tr>
              </thead>
              <tbody>
                {paginatedData.map((item) => {
                  const statusBadge = getStatusBadge(item.status);
                  const isDeposit = activeTab === 'deposits';
                  
                  return (
                    <tr key={item.id} className={`status-${item.status}`}>
                      <td className="date">{formatDate(item.created_at)}</td>
                      <td className="type">
                        {isDeposit ? (
                          <span className="badge-deposit">💳 Пополнение</span>
                        ) : (
                          <span className="badge-withdrawal">💰 Вывод</span>
                        )}
                      </td>
                      <td className="amount">
                        {isDeposit ? (
                          <strong>€{parseFloat(item.amount_eur).toFixed(2)}</strong>
                        ) : (
                          <strong>${parseFloat(item.amount_usdt).toFixed(2)}</strong>
                        )}
                      </td>
                      <td className="rate">
                        <small>{parseFloat(item.exchange_rate_applied).toFixed(4)}</small>
                      </td>
                      <td className="status">
                        <span 
                          className="status-badge"
                          style={{ backgroundColor: statusBadge.color }}
                          title={statusBadge.text}
                        >
                          {statusBadge.icon} {statusBadge.text}
                        </span>
                      </td>
                      <td className="details">
                        <button 
                          className="btn-details"
                          onClick={() => {
                            const message = isDeposit
                              ? `📋 Пополнение #${item.reference_invoice}\n€${parseFloat(item.amount_eur).toFixed(2)} → ${parseFloat(item.amount_usdt).toFixed(2)} DAR\nКомиссия: €${parseFloat(item.bank_fee_eur).toFixed(2)}`
                              : `📋 Вывод #${item.request_number}\n$${parseFloat(item.amount_usdt).toFixed(2)} → €${parseFloat(item.amount_eur).toFixed(2)}\nНа: ${item.iban_to}`;
                            alert(message);
                          }}
                        >
                          👁️ Подробнее
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          {/* Пагинация */}
          {totalPages > 1 && (
            <div className="pagination">
              <button 
                onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
                disabled={currentPage === 1}
              >
                ← Назад
              </button>
              <span className="page-info">
                Страница {currentPage} из {totalPages}
              </span>
              <button 
                onClick={() => setCurrentPage(Math.min(totalPages, currentPage + 1))}
                disabled={currentPage === totalPages}
              >
                Вперёд →
              </button>
            </div>
          )}
        </>
      )}

      {/* Пусто */}
      {!loading && !error && paginatedData.length === 0 && (
        <div className="empty-state">
          <p>📭 Нет операций для отображения</p>
          <small>
            {activeTab === 'deposits' 
              ? 'Начните пополнение счёта через EUR'
              : 'Начните вывод через USDT'
            }
          </small>
        </div>
      )}

      {/* Статистика */}
      {!loading && !error && filteredData.length > 0 && (
        <div className="statistics">
          <div className="stat-card">
            <span className="label">Всего операций:</span>
            <strong>{filteredData.length}</strong>
          </div>
          <div className="stat-card">
            <span className="label">Сумма:</span>
            <strong>
              {activeTab === 'deposits'
                ? `€${filteredData.reduce((sum, item) => sum + parseFloat(item.amount_eur || 0), 0).toFixed(2)}`
                : `$${filteredData.reduce((sum, item) => sum + parseFloat(item.amount_usdt || 0), 0).toFixed(2)}`
              }
            </strong>
          </div>
          <div className="stat-card">
            <span className="label">Успешных:</span>
            <strong>{filteredData.filter(item => item.status === 'completed').length}</strong>
          </div>
          <div className="stat-card">
            <span className="label">В ожидании:</span>
            <strong>{filteredData.filter(item => item.status === 'pending').length}</strong>
          </div>
        </div>
      )}
    </div>
  );
};

export default ExchangeHistory;
